import type { MouseEvent } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../api/biApi";
import type { SmartViewArtifact } from "../../../../api/types/smartViewArtifactsTypes";
import type { ReportGroup } from "../../../../api/types/svTypes";
import useClientContext from "../../../../context/useClientContext";
import ReportSelectorButton from "../sv/components/common/report-selector/ReportSelectorButton";
import type { ReportSelectorItem } from "../sv/components/common/report-selector/reportSelectorHelpers";
import ReportSelectorMenuWithGroupedList from "../sv/components/common/report-selector/ReportSelectorMenuWithGroupedList";

const listArtifacts = withErrorHandling(biApi.smartViewArtifacts.list);

const getArtifactGroups = withErrorHandling(biApi.smartViewArtifactGroups.getAccessibleReportGroups);

const toSelectorItem = ({ id, name, groupId }: SmartViewArtifact): ReportSelectorItem => ({
  reportId: id,
  name,
  groupId,
});

interface Props {
  artifactId: string;
  artifactName: string;
  title: string;
  mode: "view" | "edit";
}

const ArtifactSelector = ({ artifactId, artifactName, title, mode }: Props) => {
  const { clientCode, title: companyName } = useClientContext();
  const { sendNotificationError } = useNotificationContext();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<ReportSelectorItem[]>([]);
  const [groups, setGroups] = useState<ReportGroup[]>();

  const handleOpenMenu = async (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setItems([]);
    setGroups(undefined);
    setIsLoading(true);
    const [artifactsResponse, groupsResponse] = await Promise.all([listArtifacts(), getArtifactGroups(clientCode)]);
    const [artifacts, artifactsError] = artifactsResponse;
    const [artifactGroups, groupsError] = groupsResponse;
    setIsLoading(false);
    if (artifactsError || groupsError) {
      logError(artifactsError ?? groupsError, "[ArtifactSelector] loadDashboards");
      sendNotificationError("Failed to load dashboards");
      return;
    }
    setItems(artifacts.map(toSelectorItem));
    setGroups(artifactGroups);
  };

  const handleCloseMenu = () => setAnchorEl(undefined);

  const handleSelectArtifact = (selectedId: string) => {
    setAnchorEl(undefined);
    if (selectedId !== artifactId) {
      navigate(`../../${selectedId}/${mode}`);
    }
  };

  return (
    <>
      <ReportSelectorButton
        companyName={companyName}
        reportName={title}
        isMenuOpened={anchorEl !== undefined}
        readonlyMode={false}
        onOpenReportSelectorMenu={handleOpenMenu}
      />
      {anchorEl && (
        <ReportSelectorMenuWithGroupedList
          anchorEl={anchorEl}
          clientCode={clientCode}
          currentReportName={artifactName}
          groups={groups && { [clientCode]: groups }}
          reports={items}
          isLoading={isLoading}
          itemType="dashboard"
          onClose={handleCloseMenu}
          onSelectNewReport={handleSelectArtifact}
        />
      )}
    </>
  );
};

export default ArtifactSelector;
